import {uniqBy} from 'lodash';
import {connect} from 'react-redux';

import {getTeammates} from '../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {findMentionedTeammatesForAliases} from '../../../../helpers/models/commentHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {AppState} from '../../../../store/appState';
import {
  makeGetConversationTeamInboxes,
  OptionalConversationSelector,
} from '../../../../store/data/conversations/conversationsSelectors';
import {makeGetConversationParticipants} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {CommentComposerVisibility} from './commentComposerVisibility';

/*
 * Props.
 */

interface CommentComposerVisibilityContainerProps {
  conversation: ConversationModel;
  mentionedAliases?: ReadonlyArray<string>;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: CommentComposerVisibilityContainerProps) {
  return props.conversation;
}

function getMentionedAliases(_state: AppState, props: CommentComposerVisibilityContainerProps) {
  return props.mentionedAliases || [];
}

type MentionedAliasesSelector = AppParametricSelector<
  CommentComposerVisibilityContainerProps,
  ReadonlyArray<string>
>;
function makeGetMentionedTeammates(getMentionedAliasesSelector: MentionedAliasesSelector) {
  return createSelector(
    [getMentionedAliasesSelector, getTeammates, getUserId],
    (mentionedAliases, teammates, userId) =>
      findMentionedTeammatesForAliases(userId, teammates, mentionedAliases),
  );
}

function makeGetSignificantTeammates(
  getConversationSelector: OptionalConversationSelector<CommentComposerVisibilityContainerProps>,
  getMentionedAliasesSelector: MentionedAliasesSelector,
) {
  const getParticipants = makeGetConversationParticipants(getConversationSelector);
  const getMentionedTeammates = makeGetMentionedTeammates(getMentionedAliasesSelector);
  return createSelector([getParticipants, getMentionedTeammates], (participants, mentionedTeammates) =>
    uniqBy([...participants, ...mentionedTeammates], (t) => t.id),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CommentComposerVisibilityContainerProps>()(() => {
  const getMentionedAliasesShallow = wrapInShallowSelector(getMentionedAliases);

  return {
    significantTeammates: makeGetSignificantTeammates(getConversation, getMentionedAliasesShallow),
    significantInboxes: makeGetConversationTeamInboxes(getConversation),
    userId: getUserId,
  };
});

export const CommentComposerVisibilityContainer = connect(mapStateToProps)(CommentComposerVisibility);
